import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { Chip, TableCell, TableRow } from '@mui/material';
import { useGetListWhatsGoingOnMutation, useDeleteWhatsGoingOnMutation } from 'store/whatsgoingon';
import Content from 'components/content/content';
import Notification from 'components/notification';
import GLOBAL from 'modules/global';
import CONSTANT from 'modules/constant';
import type { WhatsGoingOnListItemProps, ErrorProps } from 'modules/types';
import Create from "pages/whatsgoingon/create/create";
import Edit from "pages/whatsgoingon/edit/edit";
import ActionMenu from "components/actionMenu";

const WhatsGoingOnList = () => {
  const [getWhatsGoingOnList, whatsGoingOnList] = useGetListWhatsGoingOnMutation();
  const [deleteItem, deleteResult] = useDeleteWhatsGoingOnMutation();

  const [dialogCreate, setDialogCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [payload, setPayload] = useState({
    start: 0,
    length: CONSTANT.DEFAULT_PAGINATION_PER_PAGE,
    Search: '',
  });

  const formatDate = (e: string) => (!isEmpty(e) ? moment(e).format('DD-MM-YYYY HH:mm') : '-');

  const onSearch = (value: string) => setPayload({ ...payload, start: 0, Search: value });
  const onChangePage = (value: number) => setPayload({ ...payload, start: value - 1 });
  const onChangePerPage = (value: number) => setPayload({ ...payload, length: value });


  const onResync = () => {
    const formData = new FormData();
    formData.append('start', payload.start.toString());
    formData.append('length', payload.length.toString());
    if (!isEmpty(payload.Search)) formData.append('Search', payload.Search);
    getWhatsGoingOnList(formData);
  };

  const columns = [
    { column: 'Name', label: 'Name', key: 'name' },
    { column: 'Logo', label: 'Logo', key: 'logoImage', render: (row: WhatsGoingOnListItemProps) => row.logoImage?.split('/').slice(4).join('/') || '-' },
    { column: 'Has Button', label: 'Has Button', key: 'isButton', render: (row: WhatsGoingOnListItemProps) => (row.isButton ? 'Yes' : 'No') },
    { column: 'URL Button', label: 'URL Button', key: 'urlButton' },
    {
      column: 'Status', label: 'Status', key: 'isActive', render: (row: WhatsGoingOnListItemProps) => {
        const color: ComponentProps<typeof Chip>['color'] = row.isActive ? 'success' : 'error';
        return <Chip label={row.isActive ? 'Active' : 'Inactive'} color={color} />;
      }
    },
    { column: 'Created At', label: 'Created At', key: 'createdAt', render: (row: WhatsGoingOnListItemProps) => formatDate(row.createdAt) },
  ];

  const tableHeadTitles = columns.map(c => c.label);
  const totalPage = whatsGoingOnList.data && GLOBAL.tableTotalPage(whatsGoingOnList.data.recordsTotal);
  const numbers = GLOBAL.tableNumber(payload.start);

  function handleClose() {
    setSelectedId(null)
  }

  async function handleEditClick(item: WhatsGoingOnListItemProps) {
    setSelectedId(item.id as number)
  }

  useEffect(() => {
    onResync();
  }, [payload]);

  return (
    <>
      <Content
        title="What's Going On"
        tableHeadTitles={tableHeadTitles}
        onSearch={onSearch}
        onResync={onResync}
        onAdditional={() => setDialogCreate(true)}
        additionalTitle="Create What's Going On"
        additionalPrefixIcon="Create"
        pagination={onChangePage}
        onChangePerPage={onChangePerPage}
        totalPage={totalPage}
        totalRecords={whatsGoingOnList.data?.recordsTotal}
        page={payload.start + 1}
        isLoading={whatsGoingOnList.isLoading}
      >
        {whatsGoingOnList.isSuccess && whatsGoingOnList.data?.data?.map((row: WhatsGoingOnListItemProps, index: number) => (
          <TableRow key={index}>
            <TableCell>{numbers[index] + 1}</TableCell>
            <TableCell>
              <ActionMenu
                id={`inbox-${row.id}`}
                menus={[
                  {
                    label: 'Edit',
                    onClick: () => handleEditClick(row)
                  },
                  {
                    label: 'Delete',
                    color: 'red',
                    disabled: deleteResult.isLoading,
                    onConfirm: async () => deleteItem(row.id as number)
                  }
                ]}
              />
            </TableCell>
            {columns.map(col => (<TableCell key={col.key}>{col.render ? col.render(row) : (row[col.key as keyof typeof row] as string) || '-'}</TableCell>))}
          </TableRow>
        ))}
      </Content>
      <Create open={dialogCreate} onClose={() => setDialogCreate(false)} />
      <Edit id={selectedId as number} open={!!selectedId} onClose={handleClose} />
      <Notification open={!deleteResult.isLoading && !deleteResult.isUninitialized} onClose={() => (deleteResult.isError ? deleteResult.reset() : location.reload())} isError={Boolean(deleteResult.error) && deleteResult.isError} message={GLOBAL.returnExceptionMessage(deleteResult.isError, deleteResult.error as ErrorProps)} />
    </>
  );
};

export default WhatsGoingOnList